import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FileText } from 'lucide-react'

import { financeApi } from '@/services/finance'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty } from '@/components/ui/empty'
import { withServiceInstanceId } from '@/lib/service-instance'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { Particular } from '@/types'

type JobInventoryPanelProps = {
  jobId: string
  serviceInstanceId?: string | null
}

type ParticularPurchaseStatusKey =
  | ''
  | 'draft'
  | 'requested'
  | 'order_placed'
  | 'arrived'
  | 'approved'
  | 'rejected'
  | 'pending'
  | 'purchased'

const normalizePurchaseStatus = (value?: string | null): ParticularPurchaseStatusKey => {
  const raw = (value || '').trim().toLowerCase()
  if (raw === 'pending') return 'requested'
  if (raw === 'purchased') return 'approved'
  if (['', 'draft', 'requested', 'order_placed', 'arrived', 'approved', 'rejected'].includes(raw)) {
    return raw as ParticularPurchaseStatusKey
  }
  return ''
}

const PURCHASE_STATUS_LABELS: Record<ParticularPurchaseStatusKey, string> = {
  '': 'Not applicable',
  draft: 'Draft',
  requested: 'Requested',
  order_placed: 'Order placed',
  arrived: 'Arrived',
  approved: 'Approved',
  rejected: 'Rejected',
  pending: 'Requested',
  purchased: 'Approved',
}

const getPurchaseStatusLabel = (particular: Particular) => {
  if (particular.purchase_status_label) {
    return particular.purchase_status_label
  }
  return PURCHASE_STATUS_LABELS[normalizePurchaseStatus(particular.purchase_status)] || 'Draft'
}

const getPurchaseStatusBadgeClass = (status: ParticularPurchaseStatusKey) => {
  switch (status) {
    case 'requested':
      return 'border-sky-200 bg-sky-50 text-sky-700'
    case 'approved':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'order_placed':
      return 'border-violet-200 bg-violet-50 text-violet-700'
    case 'arrived':
      return 'border-emerald-300 bg-emerald-100 text-emerald-800'
    case 'rejected':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'draft':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    default:
      return 'border-slate-200 bg-slate-50 text-slate-600'
  }
}

const formatQuantity = (value: number | string) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return String(value)
  }
  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(2)
}

export function JobInventoryPanel({ jobId, serviceInstanceId }: JobInventoryPanelProps) {
  const particularsQuery = useQuery({
    queryKey: ['job-particulars', jobId],
    queryFn: () => financeApi.getParticulars({ job_id: jobId, page_size: 200 }),
    enabled: Boolean(jobId),
    staleTime: 30_000,
  })

  const particulars = particularsQuery.data?.results ?? []
  const inventoryHref = withServiceInstanceId(
    `/job/particulars?job_id=${encodeURIComponent(jobId)}`,
    serviceInstanceId,
  )

  if (particularsQuery.isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    )
  }

  if (!particulars.length) {
    return (
      <Empty className="py-10">
        <FileText className="mb-4 h-10 w-10 text-muted-foreground/60" />
        <p className="text-lg font-medium">No inventory items yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Add materials, supplies, and billable line items from the inventory page.
        </p>
        <Button className="mt-4" asChild>
          <Link to={inventoryHref}>
            <FileText className="mr-2 h-4 w-4" />
            Add inventory
          </Link>
        </Button>
      </Empty>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border/70">
      <table className="w-full text-sm">
        <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Item</th>
            <th className="px-4 py-3">Responsible</th>
            <th className="px-4 py-3">Work Day</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Qty</th>
            <th className="px-4 py-3">Unit Price</th>
            <th className="px-4 py-3">Line Total</th>
          </tr>
        </thead>
        <tbody>
          {particulars.map((particular) => {
            const quantityValue = Number(particular.quantity || 0)
            const unitPriceValue = Number(particular.cost || particular.unit_price || 0)
            const totalPriceValue = Number(particular.line_total || quantityValue * unitPriceValue)
            const purchaseStatus = normalizePurchaseStatus(particular.purchase_status)

            return (
              <tr key={particular.id} className="border-t border-border/60">
                <td className="px-4 py-3">
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">{particular.title || 'Untitled line item'}</p>
                    {particular.category_name ? (
                      <Badge variant="outline" className="border-border/80 bg-muted/25 text-[10px] font-normal">
                        {particular.category_name}
                      </Badge>
                    ) : null}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {particular.responsible_worker_name || 'Unassigned'}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {particular.work_day_entry ? (
                    <div className="space-y-0.5">
                      <p>{particular.work_day_entry_work_date ? formatDate(particular.work_day_entry_work_date) : 'Linked'}</p>
                      {particular.work_day_entry_worker_name ? (
                        <p className="text-xs">{particular.work_day_entry_worker_name}</p>
                      ) : null}
                    </div>
                  ) : (
                    'Unlinked'
                  )}
                </td>
                <td className="px-4 py-3">
                  {particular.to_be_purchased ? (
                    <Badge variant="outline" className={getPurchaseStatusBadgeClass(purchaseStatus)}>
                      {getPurchaseStatusLabel(particular)}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">No purchase</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{formatQuantity(particular.quantity)}</td>
                <td className="px-4 py-3 tabular-nums">{formatCurrency(unitPriceValue)}</td>
                <td className="px-4 py-3 font-medium tabular-nums">{formatCurrency(totalPriceValue)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
